import React, { memo, useRef, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { Icon } from '@Sage/hoi-poi-ui';
import Scrollbar from 'components/ScrollBar';
import './styles.scss';

const HOVER_SCROLL_STEP_PX = 10;
const HOVER_SCROLL_INTERVAL_MS = 50;
/** ~one pipeline status per click (StatusPipelineWidget). */
const CLICK_SCROLL_JUMP_PX = 70;
/** Firefox reports fractional scrollLeft; strict equality leaves chevrons visible at the edge. */
const SCROLL_EDGE_THRESHOLD_PX = 2;

const HorizontalScroll = memo(({ className, width, children, scrollbarRef }) => {
    const rootClassName = classnames(className, 'fm-horizontal-scroll');
    const scrollbarsRef = useRef(null);
    const hoverIntervalRef = useRef(null);

    const [scrolledToLeft, setSCrolledToLeft] = useState(false);
    const [scrolledToRight, setScrolledToRight] = useState(false);

    const clearHoverInterval = useCallback(() => {
        if (hoverIntervalRef.current != null) {
            clearInterval(hoverIntervalRef.current);
            hoverIntervalRef.current = null;
        }
    }, []);

    const getScrollView = useCallback(() => scrollbarsRef.current?.view ?? null, []);

    const getScrollLeft = useCallback(() => {
        const view = getScrollView();
        if (view) return view.scrollLeft;
        return scrollbarsRef.current?.getScrollLeft?.() ?? 0;
    }, [getScrollView]);

    const setScrollLeft = useCallback(
        (left) => {
            const scrollbars = scrollbarsRef.current;
            if (!scrollbars) return;
            const view = getScrollView();
            if (view) {
                view.scrollLeft = left;
            }
            scrollbars.scrollLeft(left);
        },
        [getScrollView],
    );

    const updateScrollEdges = useCallback(
        (values) => {
            const view = getScrollView();
            const scrollLeft = values?.scrollLeft ?? getScrollLeft();
            const scrollWidth = values?.scrollWidth ?? view?.scrollWidth ?? 0;
            const clientWidth = values?.clientWidth ?? view?.clientWidth ?? 0;
            const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

            setSCrolledToLeft(scrollLeft <= SCROLL_EDGE_THRESHOLD_PX);
            setScrolledToRight(
                maxScrollLeft <= SCROLL_EDGE_THRESHOLD_PX ||
                    scrollLeft >= maxScrollLeft - SCROLL_EDGE_THRESHOLD_PX,
            );
        },
        [getScrollLeft, getScrollView],
    );

    const scrollTo = useCallback(
        (distance) => {
            if (!distance) return;
            setScrollLeft(getScrollLeft() + distance);
            updateScrollEdges();
        },
        [getScrollLeft, setScrollLeft, updateScrollEdges],
    );

    const scrollOnClick = useCallback(
        (direction) => {
            clearHoverInterval();
            scrollTo(direction * CLICK_SCROLL_JUMP_PX);
        },
        [clearHoverInterval, scrollTo],
    );

    const scrollOnOver = useCallback(
        (distance) => {
            clearHoverInterval();
            hoverIntervalRef.current = setInterval(() => {
                scrollTo(distance);
            }, HOVER_SCROLL_INTERVAL_MS);
        },
        [clearHoverInterval, scrollTo],
    );

    const handleScrollUpdate = (values) => {
        updateScrollEdges(values);
    };

    const onMouseOut = useCallback(() => {
        clearHoverInterval();
    }, [clearHoverInterval]);

    const setScrollbarRef = (ref) => {
        if (scrollbarRef) scrollbarRef(ref);
        scrollbarsRef.current = ref;
    };

    useEffect(() => () => clearHoverInterval(), [clearHoverInterval]);

    const renderTrackHorizontal = () => {
        return <div />;
    };

    const renderThumbHorizontal = () => {
        const rightScroll = scrolledToRight ? null : (
            <div
                className="fm-horizontal-scroll__button fm-horizontal-scroll__button--right"
                onClick={() => scrollOnClick(1)}
                onMouseOver={() => scrollOnOver(HOVER_SCROLL_STEP_PX)}
                onMouseOut={onMouseOut}
            >
                <Icon name="chevronRight" size="large" />
            </div>
        );

        const leftScroll = scrolledToLeft ? null : (
            <div
                className="fm-horizontal-scroll__button fm-horizontal-scroll__button--left"
                onClick={() => scrollOnClick(-1)}
                onMouseOver={() => scrollOnOver(-HOVER_SCROLL_STEP_PX)}
                onMouseOut={onMouseOut}
            >
                <Icon name="chevronLeft" size="large" />
            </div>
        );

        return (
            <div className="fm-horizontal-scroll__container">
                {rightScroll}
                {leftScroll}
            </div>
        );
    };

    return (
        <div className={rootClassName} onMouseOut={onMouseOut}>
            <Scrollbar
                autoHeight={true}
                autoHeightMax={100}
                autoHide={false}
                hideTracksWhenNotNeeded={true}
                renderTrackHorizontal={renderTrackHorizontal}
                renderThumbHorizontal={renderThumbHorizontal}
                onUpdate={handleScrollUpdate}
                innerRef={setScrollbarRef}
            >
                <div className="fm-horizontal-scroll__children" style={{ width }}>
                    {children}
                </div>
            </Scrollbar>
        </div>
    );
});

export default HorizontalScroll;
